import { BreakdownType } from './JourneyVisitorBreakdown.constant';
import { getFlexMetricsDateRange, getWorkflow } from 'modules/workflows/ducks';
import { useSelector } from 'react-redux';
import { Spinner } from 'ui/spinner';
import { useSortRows } from 'ui/table/hooks/useSortRows';
import { useMemo, useState } from 'react';
import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import TableHeader from 'ui/tableHeader/TableHeader';
import { Table } from 'ui/table';
import type { AlignTypes } from 'ui/tableHeader';
import TableRows from 'ui/tableRows/TableRows';
import { PaginationFooter } from 'ui/paginationFooter/PaginationFooter';
// import { Button, BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { InputSearch } from 'ui/inputs/inputSearch';
// import { Export24 } from 'ui/icons';
import { BannerVariants } from 'ui/banner/types';
import { Banner } from 'ui/banner';
import { Link } from 'ui/links';
import type { JourneyVisitorBreakdownRow } from './JourneyVisitorBreakdown.types';
import {
  JourneyVisitorBreakdownContainer,
  StyledBannerBodyBase,
  StyledInfoIcon,
  JourneyVisitorActions,
  StyledCellContainer,
  CellSecondaryText,
} from './JourneyVisitorBreakdown.style';
import { useJourneyVisitorBreakdown } from './useJourneyVisitorBreakdown';
import moment from 'moment';
import { getUserProfileHref } from '../../NodeVisitorsTakeover/NodeVisitorsTable';
import { StyledCell } from '../../NodeVisitorsTakeover/NodeVisitorsTakeover.style';
import { selectUserProjectIsUuidBased } from 'core/userContext';
import styled from 'styled-components';
import { body2 } from 'ui/theme';
import { TableCell } from 'ui/tableCell';
import { determineLastPageItems } from './journeyVisitorBreakdown.utils';

export const JOURNEY_VISITOR_BREAKDOWN_TABLE_TEST_ID = 'journey-visitor-breakdown-table';

type JourneyVisitorBreakdownTableProps = {
  breakdownType: BreakdownType;
  numUserTotal: number;
  nodeId?: number;
};

export type ColumnData = {
  align: AlignTypes;
  name: string;
  value: string;
  sortable?: boolean;
  width?: number;
};

type RowData = {
  row: JourneyVisitorBreakdownRow;
};
export const Email = styled(Link)`
  ${body2};
`;

const getColumns = (breakdownType: BreakdownType, isUuidBased: boolean) => {
  const valueKey = isUuidBased ? 'userId' : 'email';
  const email = {
    id: 'user',
    name: 'User',
    align: 'flex-start',
    value: valueKey,
    sortable: true,
    RowCell: ({ row }: { row: { userId: string; email: string } }) => {
      const { userId, email } = row;

      return (
        <StyledCell>
          <StyledCellContainer>
            {isUuidBased ? (
              <Email
                target="_blank"
                href={getUserProfileHref(userId || email)}
                rel="noreferrer noopener"
              >
                {userId || email}
              </Email>
            ) : (
              <>
                <Email target="_blank" href={getUserProfileHref(email)} rel="noreferrer noopener">
                  {row.email}
                </Email>
                {userId && <CellSecondaryText>User ID {userId}</CellSecondaryText>}
              </>
            )}
          </StyledCellContainer>
        </StyledCell>
      );
    },
  };

  const journeyEntranceTime = {
    id: 'enteredAt',
    name: 'Journey Entrance Time',
    value: 'enteredAt',
    align: 'flex-start',
    sortable: true,
    RowCell: ({ row }: RowData) => (
      <TableCell>{moment(row.enteredAt).format('ddd, MMM D YYYY, [at] hh:mma')}</TableCell>
    ),
  };
  const journeyExitTime = {
    id: 'exitedAt',
    name: 'Journey Exit Time',
    value: 'exitedAt',
    align: 'flex-start',
    sortable: true,
    RowCell: ({ row }: RowData) => (
      <TableCell>{moment(row.exitedAt).format('ddd, MMM D YYYY, [at] hh:mma')}</TableCell>
    ),
  };
  const exitReason = {
    id: 'exitReason',
    name: 'Exit Reason',
    value: 'exitReason',
    align: 'flex-start',
    sortable: true,
    RowCell: ({ row }: RowData) => <TableCell>{row.exitReason}</TableCell>,
  };
  const exitNodeId = {
    id: 'exitNodeId',
    name: 'Exit node ID',
    value: 'exitNodeId',
    align: 'flex-start',
    sortable: true,
    RowCell: ({ row }: RowData) => <TableCell>{row.exitNodeId}</TableCell>,
  };

  const baseColumns = [email, journeyEntranceTime];
  switch (breakdownType) {
    case BreakdownType.VisitorsEntered:
      return baseColumns;
    case BreakdownType.VisitorsExitedByRule:
      return [...baseColumns, journeyExitTime, exitReason, exitNodeId];
    case BreakdownType.VisitorsExited:
      return [...baseColumns, journeyExitTime, exitNodeId];
    default:
      return [];
  }
};

export const JourneyVisitorBreakdownTable = ({
  breakdownType,
  nodeId,
  numUserTotal,
}: JourneyVisitorBreakdownTableProps) => {
  const uuidBased = useSelector(selectUserProjectIsUuidBased) as boolean;
  const workflow = useSelector(getWorkflow);
  const [searchFilter, setSearchFilter] = useState('');
  const [sortBy, setSortBy] = useState('email');
  const [sortDirection, setSortDirection] = useState(SORT_DIRECTIONS.DESCENDING);
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const FlexDate = useSelector(getFlexMetricsDateRange);
  const { startDateString, endDateString } = FlexDate;

  const itemsPerPageForQuery = useMemo(() => {
    return determineLastPageItems(currentPage, numUserTotal, itemsPerPage);
  }, [itemsPerPage, numUserTotal, currentPage]);
  const { journeyVisitorBreakdown, isLoading } = useJourneyVisitorBreakdown({
    workflowId: workflow?.id,
    nodeId,
    breakdownType,
    currentPage,
    startTime: startDateString,
    endTime: endDateString,
    size: itemsPerPageForQuery,
  });

  const columns = getColumns(breakdownType, uuidBased) as ColumnData[];
  const sortedData = useSortRows<JourneyVisitorBreakdownRow>(
    journeyVisitorBreakdown,
    sortBy as keyof JourneyVisitorBreakdownRow,
    sortDirection,
  );

  const filteredRows = useMemo(() => {
    if (!searchFilter) return sortedData;
    return sortedData.filter((row: JourneyVisitorBreakdownRow) => {
      return (
        row?.email?.toString().toLowerCase().includes(searchFilter.toLowerCase()) ||
        row?.userId?.toString().toLowerCase().includes(searchFilter.toLowerCase())
      );
    });
  }, [sortedData, searchFilter]);

  // const onExport = () => {
  //   const headers = columns.map((column) => ({ label: column.name.toString(), key: column.value }));
  //   downloadAsCsvFileWithHeaders({
  //     data: filteredRows,
  //     headers: headers,
  //     fileName: `${workflow?.name || 'journey'}-visitor-breakdown-${new Date().toISOString()}.csv`,
  //   });
  //   dispatch(addPositiveToast('Successfully exported journey visitors'));
  // };

  return (
    <>
      <JourneyVisitorBreakdownContainer data-test={JOURNEY_VISITOR_BREAKDOWN_TABLE_TEST_ID}>
        <Banner variant={BannerVariants.INFO} isSubtle>
          <StyledBannerBodyBase>
            <StyledInfoIcon />
            Showing users since journey event tracking was turned.{' '}
            <Link
              target="_blank"
              rel="noopener noreferrer"
              hasOpenNewTabIcon
              href="https://support.iterable.com/hc/articles/10044063369492#tracking-journey-events"
            >
              Learn more.
            </Link>
          </StyledBannerBodyBase>
        </Banner>
        <JourneyVisitorActions>
          {/* <Button
            isDisabled={!filteredRows.length}
            size={BUTTON_SIZES.LARGE}
            variant={BUTTON_VARIANTS.SECONDARY}
            onClick={onExport}
          >
            <Export24 />
            Export
          </Button> */}
          <InputSearch
            id="search-users"
            placeholder="Search users"
            value={searchFilter}
            onChange={(value: string) => setSearchFilter(value)}
          />
        </JourneyVisitorActions>
      </JourneyVisitorBreakdownContainer>
      <Table>
        {isLoading ? (
          <Spinner />
        ) : (
          <>
            <TableHeader
              columns={columns}
              setSortDirection={(nextSortDirection: string) => {
                setSortDirection(
                  nextSortDirection as (typeof SORT_DIRECTIONS)[keyof typeof SORT_DIRECTIONS],
                );
              }}
              setSortedBy={setSortBy}
              sortDirection={sortDirection}
              sortedBy={sortBy}
            />
            <TableRows idKey="idKey" rows={filteredRows} columns={columns} />{' '}
          </>
        )}
      </Table>
      <PaginationFooter
        currentPage={currentPage === 0 ? 1 : currentPage}
        itemsPerPage={itemsPerPage}
        numItems={numUserTotal}
        maxPages={20}
        pageSizes={[10, 20, 30, 40, 50]}
        onUpdate={({ page, pageSize }) => {
          setCurrentPage(page);
          setItemsPerPage(pageSize);
        }}
      />
    </>
  );
};
